import React, { useState, useEffect, useCallback } from 'react';
import { ProductReview } from '../../domain/types';
import { partnerService } from '../../domain/services/partnerService';
import { useToast } from '../../state/ToastContext';
import { logger } from '../../infrastructure/logger';
import { ReviewForm } from './ReviewForm';
import { ReviewsList } from './ReviewsList';
import { ReviewData, ReportReason } from './types';

interface ReviewsSectionProps {
  productId: string;
  shopId?: string;
  partnerId?: string;
}

export const ReviewsSection: React.FC<ReviewsSectionProps> = ({
  productId,
  shopId,
  partnerId,
}) => {
  const { addToast } = useToast();
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      const data = await partnerService.getProductReviews(productId);
      setReviews(data);
    } catch (error) {
      logger.error('Error loading reviews', error instanceof Error ? error : new Error(String(error)));
      addToast('Error al cargar reseñas', 'error');
    } finally {
      setLoading(false);
    }
  }, [productId, addToast]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleSubmitReview = async (reviewData: ReviewData) => {
    setSubmittingReview(true);
    try {
      // Note: Add review submission service call here
      // await partnerService.submitProductReview(reviewData);
      addToast('Reseña enviada correctamente', 'success');
      setShowReviewForm(false);
      await loadReviews(); // Refresh reviews
    } catch (error) {
      logger.error('Error submitting review', error instanceof Error ? error : new Error(String(error)));
      addToast('Error al enviar la reseña', 'error');
      throw error;
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleMarkHelpful = async (reviewId: string) => {
    try {
      // Note: Add mark helpful service call here
      // await partnerService.markReviewHelpful(reviewId);
      addToast('Gracias por tu voto', 'success');
    } catch (error) {
      logger.error('Error marking review helpful', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  };

  const handleReport = async (reviewId: string, reason: ReportReason, description?: string) => {
    try {
      // Note: Add report service call here
      // await partnerService.reportReview(reviewId, reason, description);
      addToast('Reporte enviado correctamente', 'success');
    } catch (error) {
      logger.error('Error reporting review', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  };

  // Calculate statistics
  const avgRating = reviews.length > 0 
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length 
    : 0;

  const ratingDistribution = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => r.rating === star).length,
    percentage: reviews.length > 0 ? (reviews.filter(r => r.rating === star).length / reviews.length) * 100 : 0
  }));

  if (loading) {
    return (
      <section className="py-8">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8">
{/* Header */}
    <div className="flex items-center justify-between mb-6">
      <div>
        <h3 className="text-xl font-bold">Reseñas</h3>
        {reviews.length > 0 && (
          <div className="flex items-center gap-2 mt-1">
            <span className="text-2xl font-black">{avgRating.toFixed(1)}</span>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map(star => (
                <span
                  key={star}
                  className={`material-symbols-outlined text-sm ${
                    star <= Math.round(avgRating) ? 'filled text-yellow-400' : 'text-gray-300'
                  }`}
                >
                  star
                </span>
              ))}
            </div>
            <span className="text-sm text-text-secondary">({reviews.length})</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <a
          href={`/product/${productId}/reviews`}
          className="px-4 py-2 border border-primary text-primary font-medium rounded-lg hover:bg-primary/10 transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-sm">visibility</span>
          Ver todas
        </a>
        <button
          onClick={() => setShowReviewForm(!showReviewForm)}
          className="px-4 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-sm">edit</span>
          Escribir
        </button>
      </div>
    </div>

      {/* Rating Distribution */}
      {reviews.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl p-4 border border-gray-200 dark:border-white/10">
            <h4 className="font-medium text-sm mb-3 text-text-secondary">Distribución de calificaciones</h4>
            <div className="space-y-2">
              {ratingDistribution.map(({ star, count, percentage }) => (
                <div key={star} className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 w-3">{star}</span>
                  <span className="material-symbols-outlined text-yellow-500 text-sm">star</span>
                  <div className="flex-1 bg-gray-200 dark:bg-white/10 rounded-full h-2 relative">
                    <div 
                      className="bg-yellow-500 h-2 rounded-full transition-all" 
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-6 text-right">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-surface-light dark:bg-surface-dark rounded-xl p-4 border border-gray-200 dark:border-white/10">
            <h4 className="font-medium text-sm mb-3 text-text-secondary">Resumen</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">⭐ Excelente</span>
                <span className="font-bold text-green-600">{reviews.filter(r => r.rating === 5).length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">👍 Recomendado</span>
                <span className="font-bold text-blue-600">
                  {reviews.length > 0 ? Math.round((reviews.filter(r => r.rating >= 4).length / reviews.length) * 100) : 0}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">📸 Con fotos</span>
                <span className="font-bold text-purple-600">
                  {reviews.filter(r => r.images && r.images.length > 0).length}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Form */}
      {showReviewForm && (
        <div className="mb-6">
          <ReviewForm
            productId={productId}
            shopId={shopId}
            onSubmit={handleSubmitReview}
            onCancel={() => setShowReviewForm(false)}
          />
        </div>
      )}

      {/* Reviews List */}
      <ReviewsList
        reviews={reviews}
        onMarkHelpful={handleMarkHelpful}
        onReport={handleReport}
        showSort={reviews.length > 1}
      />
    </section>
  );
};
